import { useEffect, useState, useCallback } from 'react';
import { Plus, Truck, MoreHorizontal, Pencil, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  PageHeader,
  SearchInput,
  DataTable,
  Column,
  StatusBadge,
  ConfirmDialog,
  MetricCard,
  KPIGrid,
} from '@/components/common';
import {
  tmsCarriersApi,
  TmsCarrier,
  CreateCarrierDto,
  QueryCarrierParams,
  CarrierType,
  CarrierStatus,
  ServiceType,
  CarriersSummary,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { useAuth } from '@/context/AuthContext';

const CARRIER_TYPES: { value: CarrierType; label: string }[] = [
  { value: 'LTL', label: 'LTL (Less Than Truckload)' },
  { value: 'FTL', label: 'FTL (Full Truckload)' },
  { value: 'PARCEL', label: 'Parcel' },
  { value: 'AIR', label: 'Air Freight' },
  { value: 'OCEAN', label: 'Ocean Freight' },
  { value: 'RAIL', label: 'Rail' },
  { value: 'INTERMODAL', label: 'Intermodal' },
  { value: 'COURIER', label: 'Courier' },
  { value: 'SPECIALIZED', label: 'Specialized' },
];

const CARRIER_STATUSES: { value: CarrierStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'BLACKLISTED', label: 'Blacklisted' },
];

const SERVICE_TYPES: { value: ServiceType; label: string }[] = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'EXPEDITED', label: 'Expedited' },
  { value: 'OVERNIGHT', label: 'Overnight' },
  { value: 'SAME_DAY', label: 'Same Day' },
  { value: 'ECONOMY', label: 'Economy' },
  { value: 'DEDICATED', label: 'Dedicated' },
  { value: 'TEMPERATURE_CONTROLLED', label: 'Temperature Controlled' },
  { value: 'HAZMAT', label: 'Hazmat' },
  { value: 'OVERSIZED', label: 'Oversized' },
];

interface CarrierFormData {
  code: string;
  name: string;
  legalName: string;
  type: CarrierType;
  primaryContact: string;
  email: string;
  phone: string;
  website: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  mcNumber: string;
  dotNumber: string;
  scac: string;
  serviceTypes: ServiceType[];
  equipmentTypes: string;
  hazmatCertified: boolean;
  temperatureControlled: boolean;
  paymentTermsDays: number;
}

const emptyFormData: CarrierFormData = {
  code: '',
  name: '',
  legalName: '',
  type: 'FTL',
  primaryContact: '',
  email: '',
  phone: '',
  website: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'US',
  mcNumber: '',
  dotNumber: '',
  scac: '',
  serviceTypes: ['STANDARD'],
  equipmentTypes: '',
  hazmatCertified: false,
  temperatureControlled: false,
  paymentTermsDays: 30,
};

export default function Carriers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [carriers, setCarriers] = useState<TmsCarrier[]>([]);
  const [summary, setSummary] = useState<CarriersSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; carrier?: TmsCarrier }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingCarrier, setEditingCarrier] = useState<TmsCarrier | null>(null);
  const [formData, setFormData] = useState<CarrierFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);

  const fetchCarriers = useCallback(
    async (params?: QueryCarrierParams) => {
      try {
        setLoading(true);
        const response = await tmsCarriersApi.getAll({
          companyId: user?.companyId,
          ...params,
        });
        setCarriers(response.data);
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [user?.companyId]
  );

  const fetchSummary = useCallback(async () => {
    try {
      if (user?.companyId) {
        const data = await tmsCarriersApi.getSummary(user.companyId);
        setSummary(data);
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  }, [user?.companyId]);

  useEffect(() => {
    fetchCarriers();
    fetchSummary();
  }, [user?.companyId, fetchCarriers, fetchSummary]);

  useEffect(() => {
    const params: QueryCarrierParams = {
      search: searchQuery || undefined,
      status: statusFilter !== 'all' ? (statusFilter as CarrierStatus) : undefined,
      type: typeFilter !== 'all' ? (typeFilter as CarrierType) : undefined,
    };
    fetchCarriers(params);
  }, [searchQuery, statusFilter, typeFilter, fetchCarriers]);

  const handleOpenForm = (carrier?: TmsCarrier) => {
    if (carrier) {
      setEditingCarrier(carrier);
      setFormData({
        code: carrier.code,
        name: carrier.name,
        legalName: carrier.legalName || '',
        type: carrier.type,
        primaryContact: carrier.primaryContact || '',
        email: carrier.email || '',
        phone: carrier.phone || '',
        website: carrier.website || '',
        street: carrier.address?.street || '',
        city: carrier.address?.city || '',
        state: carrier.address?.state || '',
        zipCode: carrier.address?.zipCode || '',
        country: carrier.address?.country || 'US',
        mcNumber: carrier.mcNumber || '',
        dotNumber: carrier.dotNumber || '',
        scac: carrier.scac || '',
        serviceTypes: carrier.serviceTypes || ['STANDARD'],
        equipmentTypes: carrier.equipmentTypes?.join(', ') || '',
        hazmatCertified: carrier.hazmatCertified,
        temperatureControlled: carrier.temperatureControlled,
        paymentTermsDays: carrier.paymentTermsDays,
      });
    } else {
      setEditingCarrier(null);
      setFormData(emptyFormData);
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingCarrier(null);
    setFormData(emptyFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.code || !formData.name || !formData.type) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setSaving(true);
      const payload: CreateCarrierDto = {
        companyId: user!.companyId,
        code: formData.code,
        name: formData.name,
        legalName: formData.legalName || undefined,
        type: formData.type,
        primaryContact: formData.primaryContact || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        website: formData.website || undefined,
        address: {
          street: formData.street || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zipCode: formData.zipCode || undefined,
          country: formData.country || 'US',
        },
        mcNumber: formData.mcNumber || undefined,
        dotNumber: formData.dotNumber || undefined,
        scac: formData.scac || undefined,
        serviceTypes: formData.serviceTypes,
        equipmentTypes: formData.equipmentTypes
          ? formData.equipmentTypes.split(',').map((s) => s.trim())
          : [],
        hazmatCertified: formData.hazmatCertified,
        temperatureControlled: formData.temperatureControlled,
        paymentTermsDays: formData.paymentTermsDays,
      };

      if (editingCarrier) {
        await tmsCarriersApi.update(editingCarrier.id, payload);
        toast.success('Carrier updated successfully');
      } else {
        await tmsCarriersApi.create(payload);
        toast.success('Carrier created successfully');
      }

      handleCloseForm();
      fetchCarriers();
      fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.carrier) return;

    try {
      setDeleting(true);
      await tmsCarriersApi.delete(deleteDialog.carrier.id);
      toast.success('Carrier deleted successfully');
      setDeleteDialog({ open: false });
      fetchCarriers();
      fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const handleApprove = async (carrier: TmsCarrier) => {
    try {
      await tmsCarriersApi.approve(carrier.id);
      toast.success('Carrier approved successfully');
      fetchCarriers();
      fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSuspend = async (carrier: TmsCarrier) => {
    try {
      await tmsCarriersApi.suspend(carrier.id);
      toast.success('Carrier suspended');
      fetchCarriers();
      fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const columns: Column<TmsCarrier>[] = [
    {
      key: 'code',
      linkTo: (carrier) => `/tms/carriers/${carrier.id}`,
      header: 'Code',
      sortable: true,
      render: (carrier) => <span className="font-mono text-sm font-medium">{carrier.code}</span>,
    },
    {
      key: 'name',
      header: 'Carrier Name',
      sortable: true,
      render: (carrier) => (
        <div>
          <p className="font-medium">{carrier.name}</p>
          {carrier.scac && <p className="text-xs text-muted-foreground">SCAC: {carrier.scac}</p>}
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (carrier) => (
        <Badge variant="outline">
          {CARRIER_TYPES.find((t) => t.value === carrier.type)?.label || carrier.type}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (carrier) => (
        <StatusBadge
          status={carrier.status.toLowerCase().replace('_', '-')}
          label={CARRIER_STATUSES.find((s) => s.value === carrier.status)?.label || carrier.status}
        />
      ),
    },
    {
      key: 'serviceTypes',
      header: 'Services',
      render: (carrier) => (
        <div className="flex flex-wrap gap-1">
          {carrier.serviceTypes.slice(0, 2).map((service) => (
            <Badge key={service} variant="secondary" className="text-xs">
              {SERVICE_TYPES.find((s) => s.value === service)?.label || service}
            </Badge>
          ))}
          {carrier.serviceTypes.length > 2 && (
            <Badge variant="secondary" className="text-xs">
              +{carrier.serviceTypes.length - 2}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'onTimeDeliveryRate',
      header: 'On-Time %',
      sortable: true,
      render: (carrier) =>
        carrier.onTimeDeliveryRate != null ? (
          <span
            className={
              carrier.onTimeDeliveryRate >= 95
                ? 'text-success-600'
                : carrier.onTimeDeliveryRate >= 90
                  ? 'text-warning-600'
                  : 'text-danger-600'
            }
          >
            {carrier.onTimeDeliveryRate.toFixed(1)}%
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (carrier) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleOpenForm(carrier)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {carrier.status === 'PENDING_APPROVAL' && (
              <DropdownMenuItem onClick={() => handleApprove(carrier)}>
                <CheckCircle className="mr-2 h-4 w-4" />
                Approve
              </DropdownMenuItem>
            )}
            {carrier.status === 'ACTIVE' && (
              <DropdownMenuItem onClick={() => handleSuspend(carrier)}>
                <XCircle className="mr-2 h-4 w-4" />
                Suspend
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, carrier })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Carriers"
          description="Transportation providers under contract, with their service lanes and on-time performance."
          actions={
            <Button onClick={() => handleOpenForm()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Carrier
            </Button>
          }
        />
      </motion.div>

      {/* Summary Cards */}
      {summary && (
        <div className="mt-6">
          <KPIGrid columns={4}>
            <MetricCard
              label="Total Carriers"
              value={summary.totalCarriers}
              sentiment="info"
              icon={<Truck className="h-5 w-5" />}
            />
            <MetricCard label="Active" value={summary.byStatus?.ACTIVE || 0} sentiment="success" />
            <MetricCard
              label="Pending Approval"
              value={summary.byStatus?.PENDING_APPROVAL || 0}
              sentiment={(summary.byStatus?.PENDING_APPROVAL || 0) > 0 ? 'warning' : 'success'}
              subtitle="Needs review"
            />
            <MetricCard
              label="Expiring Documents"
              value={summary.expiringDocuments}
              sentiment={summary.expiringDocuments > 0 ? 'danger' : 'success'}
              subtitle="Next 30 days"
            />
          </KPIGrid>
        </div>
      )}

      {/* Data Table */}
      <motion.div variants={fadeUp} className="mt-6">
        <DataTable
          columns={columns}
          data={carriers}
          loading={loading}
          onRowClick={(carrier) => navigate(`/tms/carriers/${carrier.id}`)}
          toolbar={
            <>
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search carriers..."
                className="md:w-80"
              />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {CARRIER_STATUSES.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {CARRIER_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          }
          pagination
          exportable
          columnToggle
        />
      </motion.div>

      {/* Create/Edit Sheet */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingCarrier ? 'Edit Carrier' : 'Add New Carrier'}</SheetTitle>
            <SheetDescription>
              {editingCarrier
                ? 'Update carrier information'
                : 'Enter carrier details to add them to your network'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="mt-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold">Basic Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    placeholder="CARR001"
                    disabled={!!editingCarrier}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, type: value as CarrierType })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CARRIER_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Carrier Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="ABC Logistics"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="legalName">Legal Name</Label>
                <Input
                  id="legalName"
                  value={formData.legalName}
                  onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                  placeholder="ABC Logistics Inc."
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold">Contact Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primaryContact">Primary Contact</Label>
                  <Input
                    id="primaryContact"
                    value={formData.primaryContact}
                    onChange={(e) => setFormData({ ...formData, primaryContact: e.target.value })}
                    placeholder="John Smith"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contact@abclogistics.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://abclogistics.com"
                  />
                </div>
              </div>
            </div>

            {/* Identification */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold">Identification</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mcNumber">MC Number</Label>
                  <Input
                    id="mcNumber"
                    value={formData.mcNumber}
                    onChange={(e) => setFormData({ ...formData, mcNumber: e.target.value })}
                    placeholder="MC-123456"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dotNumber">DOT Number</Label>
                  <Input
                    id="dotNumber"
                    value={formData.dotNumber}
                    onChange={(e) => setFormData({ ...formData, dotNumber: e.target.value })}
                    placeholder="1234567"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scac">SCAC Code</Label>
                  <Input
                    id="scac"
                    value={formData.scac}
                    onChange={(e) =>
                      setFormData({ ...formData, scac: e.target.value.toUpperCase() })
                    }
                    placeholder="ABCD"
                    maxLength={4}
                  />
                </div>
              </div>
            </div>

            {/* Capabilities */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold">Capabilities</h4>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="hazmat">Hazmat Certified</Label>
                  <p className="text-xs text-muted-foreground">Can transport hazardous materials</p>
                </div>
                <Switch
                  id="hazmat"
                  checked={formData.hazmatCertified}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, hazmatCertified: checked })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="tempControl">Temperature Controlled</Label>
                  <p className="text-xs text-muted-foreground">Has refrigerated equipment</p>
                </div>
                <Switch
                  id="tempControl"
                  checked={formData.temperatureControlled}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, temperatureControlled: checked })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="equipmentTypes">Equipment Types</Label>
                <Input
                  id="equipmentTypes"
                  value={formData.equipmentTypes}
                  onChange={(e) => setFormData({ ...formData, equipmentTypes: e.target.value })}
                  placeholder="Dry Van, Reefer, Flatbed (comma separated)"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold">Address</h4>
              <div className="space-y-2">
                <Label htmlFor="street">Street</Label>
                <Input
                  id="street"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  placeholder="123 Main St"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Houston"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="TX"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="zipCode">ZIP Code</Label>
                  <Input
                    id="zipCode"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    placeholder="77001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="US"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingCarrier ? 'Update Carrier' : 'Create Carrier'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        title="Delete Carrier"
        description={`Are you sure you want to delete "${deleteDialog.carrier?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleting}
        onConfirm={handleDelete}
      />
    </motion.main>
  );
}
